import { ErrorHandler } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

export interface IErrorHandler {
  captureException: any;
  isSetup?: any;
}

export enum ERROR_HANDLER_TYPES {
  RAVEN = 'RAVEN',
  SENTRY = 'SENTRY'
}

// an error thrown from a template getter repeats on every change detection cycle,
// so the same error is reported only this many times per page load
const MAX_REPORTS_PER_ERROR = 3;

export class SentryErrorHandler implements ErrorHandler {
  readonly _sentry;
  readonly _raven;
  readonly _environment;

  private _reportsCount: { [key: string]: number } = {};

  /**
   * @param skipHttpErrors pass true when ErrorLoggerHttpInterceptor is registered: it already reports
   * every failed request (honoring sentryIgnoredApiErrors), so an unhandled HttpErrorResponse would be a duplicate
   */
  constructor(errorHandler: IErrorHandler,
              errorHandlerType: ERROR_HANDLER_TYPES,
              environment: { nodeEnv: string },
              private reloadOnChunkMissing = false,
              private skipHttpErrors = false) {
    this._environment = environment;

    if (errorHandlerType === ERROR_HANDLER_TYPES.RAVEN) {
      this._raven = errorHandler;
    }

    if (errorHandlerType === ERROR_HANDLER_TYPES.SENTRY) {
      this._sentry = errorHandler;
    }
  }

  extractError(error: any) {
    if (error && error.ngOriginalError) {
      error = error.ngOriginalError;
    }

    // We can handle messages and Error objects directly.
    if (typeof error === 'string' || error instanceof Error) {
      return error;
    }

    if (error instanceof HttpErrorResponse) {
      if (error.error instanceof Error) {
        return error.error;
      }

      if (error.error instanceof ErrorEvent) {
        return error.error.message;
      }

      if (typeof error.error === 'string') {
        return `Server returned code ${error.status} with body "${error.error}"`;
      }

      return error.message;
    }

    return new Error(error.message || error);
  }

  public handleError(error: any) {
    const extractedError = this.extractError(error) || `Handled unknown error: ${error.message || error}`;
    const chunkFailedMessage = /Loading chunk [\d]+ failed/;

    if (this._shouldReport(error, extractedError)) {
      if (this._raven) {
        if (this._raven.isSetup()) {
          this._raven.captureException(extractedError);
        }
      }

      if (this._sentry) {
        this._sentry.captureException(extractedError);
      }
    }

    if (chunkFailedMessage.test(error.message) && this.reloadOnChunkMissing) {
      window.location.reload();
    }

    if (!['production', 'prod'].includes(this._environment.nodeEnv)) {
      console.error(error);
      throw extractedError instanceof Error ? extractedError : new Error(String(extractedError));
    }
  }

  private _shouldReport(error: any, extractedError: any): boolean {
    const originalError = error && error.ngOriginalError ? error.ngOriginalError : error;
    const isHttpError = originalError instanceof HttpErrorResponse
      || (originalError && originalError.rejection instanceof HttpErrorResponse);

    if (this.skipHttpErrors && isHttpError) {
      return false;
    }

    const key = extractedError instanceof Error
      ? `${extractedError.name}: ${extractedError.message}`
      : String(extractedError);

    this._reportsCount[key] = (this._reportsCount[key] || 0) + 1;

    return this._reportsCount[key] <= MAX_REPORTS_PER_ERROR;
  }
}
